<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  useTemplateRef,
  watch,
} from 'vue';
import { useEventListener } from '@vueuse/core';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const props = defineProps({
  containerHeight: { type: Number, default: 0 },
  collapseOnShortHeight: { type: Boolean, default: false },
  expandLabel: { type: String, default: 'Reply' },
  collapseLabel: { type: String, default: 'Hide reply composer' },
});

const DEFAULT_HEIGHT = 120;
const MIN_HEIGHT = 80;
const MIN_MESSAGES_HEIGHT = 200;
const EXPAND_RATIO = 0.5;
const RESET_DELAY_MS = 120;
const SHORT_CONTAINER_HEIGHT = 620;

const wrapperRef = useTemplateRef('wrapperRef');
const surroundingHeight = ref(0);
const editorHeight = ref(DEFAULT_HEIGHT);
const isResizing = ref(false);
const isCollapsed = ref(false);
const hasUserToggledCollapse = ref(false);
const startY = ref(0);
const startHeight = ref(0);
let resetTimeoutId = null;

const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

// Measure height of elements surrounding the editor (top panel, email fields, bottom panel)
const measureSurroundingHeight = () => {
  if (wrapperRef.value) {
    surroundingHeight.value = Math.max(
      0,
      wrapperRef.value.offsetHeight - editorHeight.value
    );
  }
};

const isContainerReady = computed(() => props.containerHeight > 0);
const shouldDefaultCollapse = computed(
  () =>
    props.collapseOnShortHeight &&
    isContainerReady.value &&
    props.containerHeight < SHORT_CONTAINER_HEIGHT
);

const sizeBounds = computed(() => {
  const h = props.containerHeight;
  const s = surroundingHeight.value;
  const max = Math.max(MIN_HEIGHT, h - MIN_MESSAGES_HEIGHT - s);
  const expanded = clamp(Math.floor(h * EXPAND_RATIO - s / 2), MIN_HEIGHT, max);
  return {
    min: MIN_HEIGHT,
    max: isContainerReady.value ? max : DEFAULT_HEIGHT,
    expanded,
    default: clamp(DEFAULT_HEIGHT, MIN_HEIGHT, max),
  };
});

const clampToBounds = val =>
  clamp(val, sizeBounds.value.min, sizeBounds.value.max);

const clearDragStyles = () => {
  Object.assign(document.body.style, { cursor: '', userSelect: '' });
};

const getClientY = e => (e.touches ? e.touches[0].clientY : e.clientY);

const onResizeStart = event => {
  editorHeight.value = clampToBounds(editorHeight.value);
  measureSurroundingHeight();
  isResizing.value = true;
  startY.value = getClientY(event);
  startHeight.value = clampToBounds(editorHeight.value);
  editorHeight.value = startHeight.value;
  Object.assign(document.body.style, {
    cursor: 'row-resize',
    userSelect: 'none',
  });
};

const onResizeMove = event => {
  if (!isResizing.value) return;
  if (event.touches) event.preventDefault();
  editorHeight.value = clampToBounds(
    startHeight.value + startY.value - getClientY(event)
  );
};

const onResizeEnd = () => {
  if (!isResizing.value) return;
  isResizing.value = false;
  clearDragStyles();
};

const resetEditorHeight = () => {
  editorHeight.value = sizeBounds.value.default;
  hasUserToggledCollapse.value = false;
  isCollapsed.value = shouldDefaultCollapse.value;
};

const toggleEditorExpand = () => {
  if (isCollapsed.value) {
    // The native ReplyBox can emit a delayed size-toggle while it is being
    // hidden. Only the visible compact control should reopen the composer.
    return;
  }
  editorHeight.value = clampToBounds(editorHeight.value);
  measureSurroundingHeight();
  const { expanded, max, default: defaultHeight } = sizeBounds.value;
  const isExpanded = editorHeight.value > defaultHeight;
  // If expanded is too close to default, use max so the toggle is always noticeable
  const target = expanded - defaultHeight < 100 ? max : expanded;
  editorHeight.value = isExpanded ? defaultHeight : target;
};

const expandEditor = () => {
  hasUserToggledCollapse.value = true;
  isCollapsed.value = false;
};

const collapseEditor = () => {
  hasUserToggledCollapse.value = true;
  isCollapsed.value = true;
};

const handleMessageSent = () => {
  clearTimeout(resetTimeoutId);
  resetTimeoutId = setTimeout(resetEditorHeight, RESET_DELAY_MS);
};

onMounted(() => {
  emitter.on(BUS_EVENTS.MESSAGE_SENT, handleMessageSent);
});

onBeforeUnmount(() => {
  emitter.off(BUS_EVENTS.MESSAGE_SENT, handleMessageSent);
  clearTimeout(resetTimeoutId);
  if (isResizing.value) {
    isResizing.value = false;
    clearDragStyles();
  }
});

useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(document, 'touchmove', onResizeMove, { passive: false });
useEventListener(document, 'touchend', onResizeEnd);
useEventListener(document, 'touchcancel', onResizeEnd);
useEventListener(window, 'blur', onResizeEnd);

watch(
  shouldDefaultCollapse,
  shouldCollapse => {
    if (!hasUserToggledCollapse.value) {
      isCollapsed.value = shouldCollapse;
    }
  },
  { immediate: true }
);

defineExpose({ toggleEditorExpand, resetEditorHeight });
</script>

<template>
  <div
    ref="wrapperRef"
    class="relative resizable-editor-wrapper"
    :style="{
      '--editor-height': editorHeight + 'px',
      '--editor-min-allowed': sizeBounds.min + 'px',
      '--editor-max-allowed': sizeBounds.max + 'px',
      '--editor-height-transition': isResizing ? 'none' : '180ms ease',
    }"
  >
    <button
      v-show="isCollapsed"
      type="button"
      data-testid="compact-email-reply"
      class="mx-2 mb-2 flex h-14 w-[calc(100%-1rem)] items-center justify-between rounded-xl border border-n-weak bg-n-solid-1 px-4 text-left text-sm font-semibold text-n-slate-12 transition-colors duration-200 hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
      :aria-expanded="false"
      @click="expandEditor"
    >
      <span class="flex items-center gap-2">
        <span
          class="i-lucide-reply size-4 text-n-slate-11"
          aria-hidden="true"
        />
        {{ expandLabel }}
      </span>
      <span
        class="i-lucide-chevron-up size-4 text-n-slate-10"
        aria-hidden="true"
      />
    </button>
    <div v-show="!isCollapsed" data-testid="expanded-reply-composer">
      <div
        class="group absolute inset-x-0 -top-4 z-10 flex h-4 cursor-row-resize select-none items-center justify-center bg-gradient-to-b from-transparent from-10% dark:to-n-surface-1/80 to-n-surface-1/90 backdrop-blur-[0.01875rem]"
        @mousedown="onResizeStart"
        @touchstart.prevent="onResizeStart"
        @dblclick="resetEditorHeight"
      >
        <div
          class="w-8 h-0.5 mt-1 rounded-full bg-n-slate-6 group-hover:bg-n-slate-8 transition-colors duration-200"
          :class="{ 'bg-n-slate-8': isResizing }"
        />
      </div>
      <button
        v-if="collapseOnShortHeight"
        type="button"
        data-testid="collapse-email-reply"
        class="absolute -top-3 right-14 z-[30] flex size-8 items-center justify-center rounded-full border border-n-weak bg-n-solid-1 text-n-slate-11 shadow-sm transition-colors duration-200 hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand rtl:left-14 rtl:right-auto"
        :aria-label="collapseLabel"
        :title="collapseLabel"
        @mousedown.stop
        @click.stop="collapseEditor"
      >
        <span class="i-lucide-chevron-down size-4" aria-hidden="true" />
      </button>
      <slot />
    </div>
  </div>
</template>
